import * as React from 'react';
import type {
  DomainEntity,
  EntityDetail,
  ListEndpointResponse,
  ListEndpointPaginationLinks,
  EnvironmentKey,
  EntityStatus,
  Nullable,
} from '@web-config-app/core';
import { ConfigEntityAuthAction } from '@web-config-app/core';
import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from '@tanstack/react-query';
import { buildConfigEntityAuthHeaders } from '@web-config-app/api';
import { getEndpointParamsWithValues } from '../../utilities/get-endpoint-params-with-values/get-endpoint-params-with-values';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import {
  useEntityListParams,
  initialPaginationParams,
} from '../use-entity-list-params/use-entity-list-params';

export interface UseEntityListOptions extends Partial<UseQueryOptions> {}

type EntityListUseQueryResult = UseQueryResult<ListEndpointResponse>;

export type UseEntityListResult = Omit<EntityListUseQueryResult, 'data'> & {
  /**
   * There's a clash between `useQuery` returning a `data` property and our endpoints that ALSO include a `data` property
   * in the response. So, to avoid have `data.data`, hoist the endpoint's `response.data` to the top level here.
   */
  data?: EntityDetail[];
  /**
   * Config APIs return results pagination as an object with values corresponding to
   * specific "pages" of results the entity data
   * @example
   * {
   *   self: 'v1/config-entities?page[cursor]=0&page[size]=25',
   *   first: 'v1/config-entities?page[cursor]=0&page[size]=25',
   *   next: 'v1/config-entities?page[cursor]=1&page[size]=25',
   *   last: 'v1/config-entities?page[cursor]=23&page[size]=25',
   * }
   */
  pagination?: ListEndpointPaginationLinks;
  /**
   * It extracts query parameters (like page cursor/offset and size) from the provided `url`
   * (typically obtained from the `pagination` object's links like `next`, `prev`, etc.)
   * and updates the internal state, triggering `useQuery` to fetch the corresponding page of data.
   *
   * @param url - The pagination URL (e.g., 'v1/entities?page[size]=30&page[offset]=90')
   * for the desired page.
   */
  loadPage: (page: string) => void;
  onSearch: (search?: string) => void;
  searchQuery: Nullable<string>;
  statusFilter: Nullable<string>;
  onStatusFilterChange: (status?: string) => void;
};

/**
 * Hook that provides declarative data fetching for entity lists with built-in state management.
 *
 * This hook automatically fetches and manages entity list data using React Query. It returns
 * a **state object** with loading/error states, data, and utilities for pagination and search.
 * Unlike the imperative `useConfigEntityListQuery`, this hook fetches data automatically and
 * refetches when dependencies change.
 *
 * Use this hook when you need to display a list of entities with full state management,
 * pagination controls, and search functionality (e.g., tables, dashboards, entity browsers).
 *
 * @param params.entity - The entity configuration (DomainEntity, ConfigEntity, or DomainEntityConfiguration)
 * @param params.environment - The environment to query against
 * @param params.enabled - Whether to enable the query (default: true)
 * @param params.domainEntityType - Optional filter by domain entity type
 * @param params.domainEntityId - Optional filter by domain entity ID
 * @param params.entityStatusFilter - Optional filter by entity status
 * @param params.domainEntityAttributesQuery - Optional filter by domain entity attributes
 * @param params.options - Additional React Query options
 * @param fetchFn - Optional custom fetch function for testing
 *
 * @returns Object containing:
 * - `data`: Array of entities
 * - `pagination`: Pagination links (first, next, prev, last)
 * - `loadPage(url)`: Function to load a specific page
 * - `onSearch(query)`: Function to update search query
 * - `searchQuery`: Current search query
 * - All React Query state: `isLoading`, `error`, `isFetching`, `refetch`, etc.
 *
 * @example
 * // Display a paginated list of entities with search
 * ```typescript
 * const {
 *   data,
 *   isLoading,
 *   pagination,
 *   loadPage,
 *   onSearch,
 *   searchQuery
 * } = useEntityList({
 *   entity: myEntity,
 *   environment: 'production',
 *   entityStatusFilter: 'active'
 * });
 *
 * if (isLoading) return <Spinner />;
 *
 * return (
 *   <>
 *     <SearchInput value={searchQuery} onChange={onSearch} />
 *     <EntityTable data={data} />
 *     <Pagination links={pagination} onPageClick={loadPage} />
 *   </>
 * );
 * ```
 *
 * @example
 * // Conditionally enable fetching
 * ```typescript
 * const { data, refetch } = useEntityList({
 *   entity: myEntity,
 *   environment: 'staging',
 *   enabled: false  // Don't fetch automatically
 * });
 *
 * // Manually trigger fetch
 * const handleLoad = () => refetch();
 * ```
 *
 * @see useConfigEntityListQuery - For imperative, on-demand queries without state management
 */
export const useEntityList = (
  {
    entity,
    enabled = true,
    environment,
    domainEntityType,
    domainEntityId,
    entityStatusFilter,
    defaultStatusFilter,
    domainEntityAttributesQuery,
    options,
  }: {
    entity: DomainEntity;
    enabled?: boolean;
    environment: EnvironmentKey;
    domainEntityType?: string;
    domainEntityId?: string;
    domainEntityAttributesQuery?: string;
    options?: UseEntityListOptions;
    entityStatusFilter?: EntityStatus;
    defaultStatusFilter?: string;
  },
  fetchFn?: (path: string, options: any) => Promise<Response>,
): UseEntityListResult => {
  const { id, endpoints, apiUrl } = entity;
  const { list } = endpoints;

  const {
    offset: urlOffset,
    size: urlSize,
    searchQuery,
    statusFilter,
    loadPage,
    onSearch,
    onStatusFilterChange,
  } = useEntityListParams();

  const urlOffsetValue = domainEntityId
    ? initialPaginationParams['page[offset]']
    : urlOffset;
  const urlSizeValue = domainEntityId
    ? initialPaginationParams['page[size]']
    : urlSize;

  if (!list) {
    throw new Error(`Entity ${id} has no defined list endpoint`);
  }

  const currentEnvironmentFetch = useEnvironmentFetch(environment, apiUrl);
  const { endpointFetch } = getEntityEndpoint<ListEndpointResponse>(
    list,
    fetchFn ?? currentEnvironmentFetch,
  );

  /**
   * maintain the exact path called endpoint as the cache key
   *
   * For example, if `pageUrl` is set, use that. Otherwise use
   * the endpoint root URL
   *
   * Our endpoint also use a `data` property so we rename the `useQuery`
   * `data` property here to `queryResultData` to avoid having to use
   * `data.data` below.
   */
  const effectiveStatusFilter =
    entityStatusFilter || statusFilter || defaultStatusFilter;

  const params = React.useMemo(() => {
    const paramObjects: { [key: string]: string } = {
      ...(domainEntityId
        ? { 'filter[domainEntityID]': domainEntityId }
        : undefined),
      ...(domainEntityType
        ? { 'filter[domainEntityType]': domainEntityType }
        : undefined),
      ...(effectiveStatusFilter
        ? { 'filter[status]': effectiveStatusFilter }
        : undefined),
      ...(searchQuery
        ? { 'filter[query]': encodeURIComponent(searchQuery) }
        : undefined),
      ...(urlOffsetValue
        ? { 'page[offset]': encodeURIComponent(urlOffsetValue) }
        : undefined),
      ...(urlSizeValue
        ? { 'page[size]': encodeURIComponent(urlSizeValue) }
        : undefined),
      ...(domainEntityAttributesQuery
        ? { 'filter[domainEntityAttributes]': domainEntityAttributesQuery }
        : undefined),
    };

    return getEndpointParamsWithValues(
      Object.entries(paramObjects).map(([name, value]) => ({ name, value })),
    );
  }, [
    domainEntityId,
    domainEntityType,
    domainEntityAttributesQuery,
    effectiveStatusFilter,
    searchQuery,
    urlOffsetValue,
    urlSizeValue,
  ]);

  const headerDomainEntityType = domainEntityType ?? entity.type;
  const configEntityAuthHeader = headerDomainEntityType
    ? buildConfigEntityAuthHeaders(
        headerDomainEntityType,
        ConfigEntityAuthAction.Get,
      )
    : undefined;
  const { data: queryResultData, ...query } = useQuery({
    queryKey: [
      environment,
      domainEntityAttributesQuery,
      urlOffsetValue,
      urlSizeValue,
      domainEntityType,
      domainEntityId,
      searchQuery,
      effectiveStatusFilter,
      list.path,
    ],
    queryFn: () =>
      endpointFetch({
        params,
        headers: configEntityAuthHeader,
      }),
    enabled: enabled && !!list.path,
    retry: options?.retry,
  });

  return {
    ...query,
    data: queryResultData?.data ?? [],
    pagination: queryResultData?.links,
    loadPage,
    onSearch,
    searchQuery,
    statusFilter,
    onStatusFilterChange,
  };
};
